var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../../layout/components/loaders');
var modal = require('../../layout/components/modal');
var Model = require('../../libs/Model');
var Report = require('../../../../../models/reports/report');

var addEquipmentModal = {};

addEquipmentModal.searchValue = prop('');
addEquipmentModal.selectedEquipment = prop([]);
addEquipmentModal.allResults = prop([]);
addEquipmentModal.results = prop([]);
addEquipmentModal.reportClass = null;
addEquipmentModal.error = prop('');
addEquipmentModal.loading = prop(false);
addEquipmentModal.classFilter = prop('');
addEquipmentModal.classSearch = prop('');
addEquipmentModal.classMenuOpen = prop(false);
addEquipmentModal.page = prop(1);
addEquipmentModal.pageSize = 50;
addEquipmentModal.total = prop(0);
addEquipmentModal.totalPages = prop(0);
addEquipmentModal.hasNext = prop(false);
addEquipmentModal.locationOptions = prop([]);
addEquipmentModal.locationsLoaded = prop(false);
addEquipmentModal.locationFilters = prop({});
addEquipmentModal._searchDebounce = null;
addEquipmentModal._requestSerial = 0;
addEquipmentModal._selectionAnchorRid = null;
addEquipmentModal.recordStream = null;
addEquipmentModal.equipmentTable = null;

function isLocationRecord(record) {
  return ['Site', 'Building', 'Level', 'Room'].indexOf(record && record['@class']) >= 0;
}

function buildPrimaryLabel(record) {
  if (!record) return '';

  var name = record.name ? String(record.name).trim() : '';
  var roomNumber = record.roomNumber ? String(record.roomNumber).trim() : '';

  if (isLocationRecord(record)) {
    if (roomNumber && name) return roomNumber + ' - ' + name;
    if (roomNumber) return roomNumber;
    if (name) return name;
  }

  return name || roomNumber || record['@rid'] || '';
}

function buildPathLabel(record) {
  var path = Array.isArray(record && record['$path']) ? record['$path'].slice() : [];
  if (isLocationRecord(record) && path.length > 1) {
    path = path.filter(function (segment) {
      return !(record && segment && segment['@rid'] === record['@rid']);
    });
  }
  return path
    .map(function (segment) {
      return segment && (segment.name || segment.roomNumber || segment['@class']);
    })
    .filter(Boolean)
    .join(' / ');
}

function normalizeResultRecords(records) {
  var byRid = new Map();
  (records || []).forEach(function (record) {
    if (!record || !record['@rid']) return;
    if (!byRid.has(record['@rid'])) {
      byRid.set(record['@rid'], record);
    }
  });
  return Array.from(byRid.values());
}

function normalizeRid(value) {
  var rid = value && typeof value === 'object' ? value['@rid'] : value;
  rid = String(rid || '').trim();
  if (!rid) return '';
  return rid.charAt(0) === '#' ? rid : '#' + rid;
}

function normalizeLocationOptions(locations) {
  var seen = new Set();
  return (Array.isArray(locations) ? locations : []).map(function (location) {
    return {
      rid: normalizeRid(location && (location.rid || location['@rid'])),
      className: String((location && (location.className || location['@class'])) || ''),
      name: String((location && location.name) || ''),
      roomNumber: String((location && location.roomNumber) || ''),
      parentRid: normalizeRid(location && location.parentRid)
    };
  }).filter(function (location) {
    if (!location.rid || !location.className || seen.has(location.rid)) return false;
    seen.add(location.rid);
    return true;
  });
}

function formatLocationOptionLabel(location) {
  var name = String((location && location.name) || '').trim();
  var roomNumber = String((location && location.roomNumber) || '').trim();
  if (roomNumber && name) return roomNumber + ' - ' + name;
  return roomNumber || name || (location && location.rid) || '';
}

var locationHierarchy = ['Site', 'Building', 'Level', 'Room'];

function buildLocationSelectors(locations, rootRid, selections) {
  var options = normalizeLocationOptions(locations);
  var normalizedRootRid = normalizeRid(rootRid);
  var root = options.find(function (location) { return location.rid === normalizedRootRid; });
  if (!root) return [];

  var rootIndex = locationHierarchy.indexOf(root.className);
  if (rootIndex < 0) return [];

  var selectedByClass = selections && typeof selections === 'object' ? selections : {};
  var parentRid = root.rid;
  var selectors = [];
  for (var hierarchyIndex = rootIndex + 1; hierarchyIndex < locationHierarchy.length; hierarchyIndex += 1) {
    var className = locationHierarchy[hierarchyIndex];
    var childOptions = options.filter(function (location) {
      return location.className === className && location.parentRid === parentRid;
    }).sort(function (a, b) {
      return formatLocationOptionLabel(a).localeCompare(formatLocationOptionLabel(b), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
    if (!childOptions.length) break;

    var selectedRid = normalizeRid(selectedByClass[className]);
    if (!childOptions.some(function (location) { return location.rid === selectedRid; })) {
      selectedRid = '';
    }
    selectors.push({
      className: className,
      options: childOptions,
      selectedRid: selectedRid
    });
    if (!selectedRid) break;
    parentRid = selectedRid;
  }
  return selectors;
}

function getLocationScopeRid(locations, rootRid, selections) {
  return buildLocationSelectors(locations, rootRid, selections).reduce(function (scopeRid, selector) {
    return selector.selectedRid || scopeRid;
  }, normalizeRid(rootRid));
}

function updateLocationFilters(selections, className, selectedRid) {
  var current = selections && typeof selections === 'object' ? selections : {};
  var classIndex = locationHierarchy.indexOf(className);
  var next = {};
  locationHierarchy.forEach(function (candidateClass, candidateIndex) {
    if (candidateIndex < classIndex && current[candidateClass]) {
      next[candidateClass] = current[candidateClass];
    }
  });
  if (selectedRid) next[className] = normalizeRid(selectedRid);
  return next;
}

function normalizeSearchResponse(response, fallbackPage, fallbackPageSize) {
  if (Array.isArray(response)) {
    return {
      records: normalizeResultRecords(response),
      page: fallbackPage || 1,
      pageSize: fallbackPageSize || 15,
      total: response.length,
      totalPages: response.length ? 1 : 0,
      hasNext: false,
      complete: true,
      locations: null
    };
  }

  response = response && typeof response === 'object' ? response : {};
  var records = normalizeResultRecords(response.records);
  var pageSize = Math.max(1, Number(response.pageSize) || fallbackPageSize || 15);
  var total = Math.max(0, Number(response.total) || 0);
  var totalPages = Math.max(0, Number(response.totalPages) || (total ? Math.ceil(total / pageSize) : 0));
  return {
    records: records,
    page: Math.max(1, Number(response.page) || fallbackPage || 1),
    pageSize: pageSize,
    total: total,
    totalPages: totalPages,
    hasNext: response.hasNext === true || (totalPages > 0 && (Number(response.page) || fallbackPage || 1) < totalPages),
    complete: response.complete === true,
    locations: Array.isArray(response.locations) ? normalizeLocationOptions(response.locations) : null
  };
}

function humanizeClassName(className) {
  return String(className || '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
}

function getClassFilterOptions(config) {
  var seen = new Set();
  var classNames = (Array.isArray(config && config.allowedClasses) ? config.allowedClasses : [])
    .map(function (className) { return String(className || '').trim(); })
    .filter(function (className) {
      if (!className || seen.has(className)) return false;
      seen.add(className);
      return true;
    })
    .sort(function (a, b) {
      return humanizeClassName(a).localeCompare(humanizeClassName(b), undefined, { numeric: true, sensitivity: 'base' });
    });

  return [{ value: '', key: 'All classes' }].concat(classNames.map(function (className) {
    return {
      value: className,
      key: humanizeClassName(className)
    };
  }));
}

function filterClassOptions(options, query, limit) {
  var normalizedQuery = String(query || '').trim().toLowerCase();
  var maxResults = Math.max(1, Number(limit) || 12);
  var source = Array.isArray(options) ? options : [];
  if (!normalizedQuery) return source.slice(0, maxResults);

  var terms = normalizedQuery.split(/\s+/).filter(Boolean);
  return source.filter(function (option) {
    var searchText = (String(option && option.key || '') + ' ' + String(option && option.value || '')).toLowerCase();
    return terms.every(function (term) { return searchText.indexOf(term) >= 0; });
  }).slice(0, maxResults);
}

function getReportTypeMeta(record) {
  var type = record && record.type ? record.type : 'Custom Export';
  return Report.getTypeMeta ? Report.getTypeMeta(type) : {};
}

function getEquipmentTableConfig(record) {
  var meta = getReportTypeMeta(record);
  var ui = meta && meta.ui ? meta.ui : {};
  return ui && ui.equipmentTable ? ui.equipmentTable : {};
}

function getRecordLabel(record) {
  var config = addEquipmentModal.equipmentTable || getEquipmentTableConfig(record);
  return config.recordLabel || config.recordClass || (record && record.type) || 'Equipment';
}

function showEquipmentPage(pageNumber) {
  var records = addEquipmentModal.allResults() || [];
  var total = records.length;
  var totalPages = total ? Math.ceil(total / addEquipmentModal.pageSize) : 0;
  var requestedPage = Math.max(1, Number(pageNumber) || 1);
  var page = totalPages ? Math.min(requestedPage, totalPages) : 1;
  var start = (page - 1) * addEquipmentModal.pageSize;

  addEquipmentModal.page(page);
  addEquipmentModal.results(records.slice(start, start + addEquipmentModal.pageSize));
  addEquipmentModal.total(total);
  addEquipmentModal.totalPages(totalPages);
  addEquipmentModal.hasNext(page < totalPages);
  addEquipmentModal._selectionAnchorRid = null;
}

function searchEquipment() {
  var reportLocationRid = normalizeRid(addEquipmentModal.record && addEquipmentModal.record.locationRid);
  var requestSerial = ++addEquipmentModal._requestSerial;
  addEquipmentModal._selectionAnchorRid = null;
  addEquipmentModal.page(1);
  addEquipmentModal.loading(true);
  addEquipmentModal.error('');

  return loaders.requestWithLoader({
    loader: 'background',
    url: Model.urlForRecord(addEquipmentModal.record) + '/availableEquipment',
    method: 'GET',
    background: true,
    params: {
      search: addEquipmentModal.searchValue(),
      equipmentClass: addEquipmentModal.classFilter() || undefined,
      locationScope: getLocationScopeRid(
        addEquipmentModal.locationOptions(),
        reportLocationRid,
        addEquipmentModal.locationFilters()
      ) || undefined,
      includeLocations: addEquipmentModal.locationsLoaded() ? undefined : true,
      all: true,
      page: 1,
      pageSize: addEquipmentModal.pageSize,
      allowedClasses: addEquipmentModal.equipmentTable && addEquipmentModal.equipmentTable.restrictSearchClasses === false
        ? undefined
        : Array.isArray(addEquipmentModal.equipmentTable && addEquipmentModal.equipmentTable.allowedClasses)
        ? addEquipmentModal.equipmentTable.allowedClasses.join(',')
        : undefined
    }
  })
    .then(function (response) {
      if (requestSerial !== addEquipmentModal._requestSerial) return;
      var result = normalizeSearchResponse(response, 1, addEquipmentModal.pageSize);
      addEquipmentModal.allResults(result.records);
      showEquipmentPage(1);
      if (result.locations !== null) {
        addEquipmentModal.locationOptions(result.locations);
        addEquipmentModal.locationsLoaded(true);
      }
      var records = result.records;
      if (!(addEquipmentModal.selectedEquipment() || []).length && records.length === 1) {
        addEquipmentModal.selectedEquipment([records[0]]);
      }
      m.redraw();
    })
    .catch(function (error) {
      if (requestSerial !== addEquipmentModal._requestSerial) return;
      addEquipmentModal.allResults([]);
      addEquipmentModal.results([]);
      addEquipmentModal.total(0);
      addEquipmentModal.totalPages(0);
      addEquipmentModal.hasNext(false);
      addEquipmentModal.error(error && error.error ? error.error : 'Could not load equipment.');
      m.redraw();
    })
    .finally(function () {
      if (requestSerial !== addEquipmentModal._requestSerial) return;
      addEquipmentModal.loading(false);
      m.redraw();
    });
}

function isSelected(equipmentRecord) {
  return (addEquipmentModal.selectedEquipment() || []).some(function (selectedRecord) {
    return selectedRecord && equipmentRecord && selectedRecord['@rid'] === equipmentRecord['@rid'];
  });
}

function toggleSelection(equipmentRecord) {
  var selectedRecords = (addEquipmentModal.selectedEquipment() || []).slice();
  var existingIndex = selectedRecords.findIndex(function (selectedRecord) {
    return selectedRecord && equipmentRecord && selectedRecord['@rid'] === equipmentRecord['@rid'];
  });

  if (existingIndex >= 0) {
    selectedRecords.splice(existingIndex, 1);
  } else {
    selectedRecords.push(equipmentRecord);
  }

  addEquipmentModal.selectedEquipment(selectedRecords);
}

function setEquipmentSelected(equipmentRecords, selected) {
  var records = (equipmentRecords || []).filter(function (record) {
    return record && record['@rid'];
  });
  var recordRids = new Set(records.map(function (record) { return record['@rid']; }));
  var selectedRecords = (addEquipmentModal.selectedEquipment() || []).filter(function (record) {
    return record && (selected || !recordRids.has(record['@rid']));
  });

  if (selected) {
    var selectedRids = new Set(selectedRecords.map(function (record) { return record['@rid']; }));
    records.forEach(function (record) {
      if (selectedRids.has(record['@rid'])) return;
      selectedRids.add(record['@rid']);
      selectedRecords.push(record);
    });
  }

  addEquipmentModal.selectedEquipment(selectedRecords);
}

function selectEquipment(equipmentRecord, selectRange) {
  var visibleRecords = addEquipmentModal.results() || [];
  var targetRid = equipmentRecord && equipmentRecord['@rid'];
  var anchorRid = addEquipmentModal._selectionAnchorRid;
  var targetIndex = visibleRecords.findIndex(function (record) {
    return record && record['@rid'] === targetRid;
  });
  var anchorIndex = visibleRecords.findIndex(function (record) {
    return record && record['@rid'] === anchorRid;
  });

  if (selectRange && targetIndex >= 0 && anchorIndex >= 0) {
    var rangeStart = Math.min(anchorIndex, targetIndex);
    var rangeEnd = Math.max(anchorIndex, targetIndex);
    setEquipmentSelected(visibleRecords.slice(rangeStart, rangeEnd + 1), !isSelected(equipmentRecord));
  } else {
    toggleSelection(equipmentRecord);
  }

  addEquipmentModal._selectionAnchorRid = targetRid || null;
}

function visibleSelectedEquipment() {
  return (addEquipmentModal.results() || []).filter(isSelected);
}

function matchingSelectedEquipment() {
  return (addEquipmentModal.allResults() || []).filter(isSelected);
}

function setMatchingEquipmentSelected(selected) {
  setEquipmentSelected(addEquipmentModal.allResults() || [], selected);
  addEquipmentModal._selectionAnchorRid = null;
}

function setVisibleEquipmentSelected(selected) {
  var visibleRecords = addEquipmentModal.results() || [];
  var visibleRids = new Set(visibleRecords.map(function (record) {
    return record && record['@rid'];
  }).filter(Boolean));
  var selectedRecords = (addEquipmentModal.selectedEquipment() || []).filter(function (record) {
    return record && !visibleRids.has(record['@rid']);
  });

  if (selected) {
    selectedRecords = selectedRecords.concat(visibleRecords.filter(function (record) {
      return record && record['@rid'];
    }));
  }

  addEquipmentModal.selectedEquipment(selectedRecords);
  addEquipmentModal._selectionAnchorRid = null;
}

function selectedCellAttrs(selected) {
  return selected ? { style: { backgroundColor: '#1f4358' } } : {};
}

function renderSelectionButton(options) {
  var checked = !!options.checked;
  var mixed = !!options.mixed;
  var disabled = !!options.disabled;
  return m('button', {
    type: 'button',
    class: 'ba bw1 br1 flex items-center justify-center center',
    style: {
      width: '1.35rem',
      height: '1.35rem',
      minWidth: '1.35rem',
      padding: 0,
      borderColor: checked || mixed ? '#58abe2' : '#777',
      backgroundColor: checked || mixed ? '#58abe2' : 'transparent',
      color: checked || mixed ? '#111' : '#f4f4f4',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      lineHeight: 1
    },
    disabled: disabled,
    title: options.label,
    'aria-label': options.label,
    'aria-checked': mixed ? 'mixed' : checked ? 'true' : 'false',
    role: 'checkbox',
    'data-select-all-equipment': options.selectAll ? true : null,
    'data-equipment-select-rid': options.rid || null,
    onmousedown: function (event) {
      event.stopPropagation();
    },
    onclick: function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') {
        options.onclick(event);
      }
    },
    onkeydown: function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') {
        options.onclick(event);
      }
    }
  }, [
    checked ? m('i.f4.ion-checkmark') : mixed ? m('span.b.f4', '-') : null
  ]);
}

addEquipmentModal.editDialogue = function (record, options) {
  addEquipmentModal.record = record;
  addEquipmentModal.recordStream = options && typeof options.recordStream === 'function'
    ? options.recordStream
    : null;
  addEquipmentModal.equipmentTable = options && options.equipmentTable ? options.equipmentTable : null;
  addEquipmentModal.reportClass = getRecordLabel(record);
  addEquipmentModal.searchValue('');
  addEquipmentModal.selectedEquipment([]);
  addEquipmentModal.allResults([]);
  addEquipmentModal.results([]);
  addEquipmentModal.error('');
  addEquipmentModal.loading(false);
  addEquipmentModal.classFilter('');
  addEquipmentModal.classSearch('');
  addEquipmentModal.classMenuOpen(false);
  addEquipmentModal.page(1);
  addEquipmentModal.total(0);
  addEquipmentModal.totalPages(0);
  addEquipmentModal.hasNext(false);
  addEquipmentModal.locationOptions([]);
  addEquipmentModal.locationsLoaded(false);
  addEquipmentModal.locationFilters({});
  addEquipmentModal._requestSerial += 1;
  addEquipmentModal._selectionAnchorRid = null;
  if (addEquipmentModal._searchDebounce) {
    clearTimeout(addEquipmentModal._searchDebounce);
    addEquipmentModal._searchDebounce = null;
  }

  var submit = function () {
    var selected = addEquipmentModal.selectedEquipment() || [];
    if (!selected.length) {
      addEquipmentModal.error('Choose at least one equipment instance before saving.');
      m.redraw();
      return Promise.reject(new Error('Equipment selection is required.'));
    }

    return loaders.requestWithLoader({
      loader: 'submit',
      url: Model.urlForRecord(record) + '/addEquipment',
      method: 'PUT',
      body: {
        equipment: selected.map(function (equipmentRecord) { return equipmentRecord['@rid']; })
      }
    })
    .then(function (updatedRecord) {
      if (addEquipmentModal.recordStream && updatedRecord) {
        addEquipmentModal.recordStream(updatedRecord);
        m.redraw();
      }
      modal.vm.close();
    })
    .catch(function (error) {
      addEquipmentModal.error(error && error.error ? error.error : 'Could not add equipment to the report.');
      m.redraw();
      return Promise.reject(error);
    });
  };

  modal.vm.title('Select ' + (addEquipmentModal.reportClass || 'Equipment'));
  modal.vm.body = addEquipmentModal;
  modal.vm.buttons([
    {
      label: 'Submit',
      func: submit
    }
  ]);
  modal.vm.open({ extrawidth: true });

  return searchEquipment();
};

addEquipmentModal.view = function () {
  var selectedCount = (addEquipmentModal.selectedEquipment() || []).length;
  var classOptions = getClassFilterOptions(addEquipmentModal.equipmentTable);
  var page = addEquipmentModal.page();
  var pageSize = addEquipmentModal.pageSize;
  var total = addEquipmentModal.total();
  var totalPages = addEquipmentModal.totalPages();
  var hasNext = addEquipmentModal.hasNext();
  var resultRecords = addEquipmentModal.results() || [];
  var currentPageCount = resultRecords.length;
  var matchingCount = (addEquipmentModal.allResults() || []).length;
  var selectedMatchingCount = matchingSelectedEquipment().length;
  var allMatchingSelected = matchingCount > 0 && selectedMatchingCount === matchingCount;
  var someMatchingSelected = selectedMatchingCount > 0 && !allMatchingSelected;
  var reportLocationRid = normalizeRid(addEquipmentModal.record && addEquipmentModal.record.locationRid);
  var locationSelectors = buildLocationSelectors(
    addEquipmentModal.locationOptions(),
    reportLocationRid,
    addEquipmentModal.locationFilters()
  );
  var firstResult = total ? ((page - 1) * pageSize) + 1 : 0;
  var lastResult = total ? Math.min(page * pageSize, total) : 0;
  var visibleClassOptions = filterClassOptions(classOptions, addEquipmentModal.classSearch(), 12);
  var chooseClass = function (option) {
    option = option || { value: '', key: 'All classes' };
    addEquipmentModal.classFilter(option.value || '');
    addEquipmentModal.classSearch(option.value ? option.key : '');
    addEquipmentModal.classMenuOpen(false);
    searchEquipment();
  };
  var paginationButton = function (label, disabled, targetPage) {
    return m('button.input-reset.ba.br2.ph3.pv2.bg-transparent', {
      type: 'button',
      disabled: disabled,
      class: disabled ? 'moon-gray b--mid-gray' : 'pointer blue b--blue',
      onclick: function () {
        if (!disabled) {
          showEquipmentPage(targetPage);
          m.redraw();
        }
      }
    }, label);
  };
  return m('form.form-dark', { role: 'form' }, [
    addEquipmentModal.error() ? m('div.red.mb2', addEquipmentModal.error()) : null,
    selectedCount ? m('div.mb2.moon-gray', selectedCount + ' selected') : null,
    m('div.flex.flex-wrap.nl2.nr2.mb3', [
      m('div.w-100.w-60-l.ph2.mb2', [
        m('label.db.mb2', 'Search ' + (addEquipmentModal.reportClass || 'equipment')),
        m('input.form-control.w-100', {
          type: 'search',
          value: addEquipmentModal.searchValue(),
          placeholder: 'Name, room number, or class',
          oninput: function (ev) {
            addEquipmentModal.searchValue(ev.target.value);
            addEquipmentModal.page(1);
            if (addEquipmentModal._searchDebounce) {
              clearTimeout(addEquipmentModal._searchDebounce);
            }
            addEquipmentModal._searchDebounce = setTimeout(function () {
              searchEquipment();
            }, 250);
          }
        })
      ]),
      classOptions.length > 2 ? m('div.w-100.w-40-l.ph2.mb2', [
        m('label.db.mb2', 'Class'),
        m('div.relative', [
          m('input.form-control.w-100', {
            type: 'search',
            role: 'combobox',
            'aria-label': 'Class',
            'aria-autocomplete': 'list',
            'aria-expanded': addEquipmentModal.classMenuOpen() ? 'true' : 'false',
            value: addEquipmentModal.classSearch(),
            placeholder: 'All classes',
            onfocus: function () {
              addEquipmentModal.classMenuOpen(true);
            },
            oninput: function (event) {
              addEquipmentModal.classSearch(event.target.value);
              addEquipmentModal.classMenuOpen(true);
            },
            onkeydown: function (event) {
              if (event.key === 'Escape') {
                addEquipmentModal.classMenuOpen(false);
                return;
              }
              if (event.key === 'Enter' && visibleClassOptions.length) {
                event.preventDefault();
                chooseClass(visibleClassOptions[0]);
              }
            },
            onblur: function () {
              setTimeout(function () {
                if (!addEquipmentModal.classMenuOpen()) return;
                var selectedOption = classOptions.find(function (option) {
                  return option.value === addEquipmentModal.classFilter();
                });
                addEquipmentModal.classSearch(selectedOption && selectedOption.value ? selectedOption.key : '');
                addEquipmentModal.classMenuOpen(false);
                m.redraw();
              }, 120);
            }
          }),
          addEquipmentModal.classMenuOpen() ? m('div.absolute.left-0.right-0.z-5.ba.b--mid-gray.bg-dark-gray.br2.overflow-y-auto', {
            role: 'listbox',
            style: { top: '100%', maxHeight: '14rem' }
          }, visibleClassOptions.length ? visibleClassOptions.map(function (option) {
            var selected = option.value === addEquipmentModal.classFilter();
            return m('button.input-reset.bn.bb.b--mid-gray.w-100.tl.pa2.pointer', {
              type: 'button',
              role: 'option',
              'aria-selected': selected ? 'true' : 'false',
              class: selected ? 'bg-blue white' : 'bg-dark-gray near-white',
              onmousedown: function (event) { event.preventDefault(); },
              onclick: function () { chooseClass(option); }
            }, option.key);
          }) : m('div.pa2.moon-gray', 'No matching classes.')) : null
        ])
      ]) : null
    ]),
    locationSelectors.length ? m('div.flex.flex-wrap.nl2.nr2.mb3', locationSelectors.map(function (selector) {
      var selectorWidth = locationSelectors.length > 2 ? 'w-third-l' : 'w-50-l';
      return m('div.w-100.ph2.mb2.' + selectorWidth, [
        m('label.db.mb2', selector.className),
        m('select.form-control.w-100', {
          'aria-label': selector.className,
          value: selector.selectedRid,
          disabled: addEquipmentModal.loading(),
          onchange: function (event) {
            addEquipmentModal.locationFilters(updateLocationFilters(
              addEquipmentModal.locationFilters(),
              selector.className,
              event.target.value
            ));
            searchEquipment();
          }
        }, [
          m('option', { value: '' }, 'All ' + selector.className.toLowerCase() + 's')
        ].concat(selector.options.map(function (location) {
          return m('option', { value: location.rid }, formatLocationOptionLabel(location));
        })))
      ]);
    })) : null,
    (total || page > 1 || hasNext) ? m('div.flex.justify-between.items-center.mb2', [
      m('div.moon-gray.f6', [
        total
          ? m('div', firstResult + '\u2013' + lastResult + ' of ' + total)
          : m('div', 'Page ' + page + (currentPageCount ? ' \u00b7 ' + currentPageCount + ' results' : '')),
        totalPages > 1 ? m('div.mt1', 'Page ' + page + ' of ' + totalPages) : null
      ]),
      m('div.flex.items-center', [
        matchingCount > currentPageCount ? m('button.input-reset.bn.bg-transparent.mr3', {
          type: 'button',
          disabled: addEquipmentModal.loading(),
          class: addEquipmentModal.loading() ? 'moon-gray' : 'blue pointer',
          'data-select-all-matching-equipment': true,
          onclick: function () {
            if (!addEquipmentModal.loading()) {
              setMatchingEquipmentSelected(!allMatchingSelected);
            }
          }
        }, allMatchingSelected
          ? 'Clear ' + matchingCount + ' matching'
          : 'Select all ' + matchingCount + ' matching') : null,
        m('div.mr2', paginationButton('Previous', addEquipmentModal.loading() || page <= 1, page - 1)),
        paginationButton('Next', addEquipmentModal.loading() || !hasNext, page + 1)
      ])
    ]) : null,
    m('div.ba.b--mid-gray.br2.overflow-auto', {
      'aria-label': 'Equipment results',
      'aria-busy': addEquipmentModal.loading() ? 'true' : 'false',
      style: {
        maxHeight: locationSelectors.length
          ? 'max(10rem, calc(100vh - 30rem))'
          : 'max(12rem, calc(100vh - 25rem))'
      }
    }, [
      m('table.w-100.f4', { style: { minWidth: '42rem', borderCollapse: 'collapse' } }, [
        m('colgroup', [
          m('col', { style: { width: '2.5rem' } }),
          m('col', { width: '30%' }),
          m('col', { width: '22%' }),
          m('col')
        ]),
        m('thead', [
          m('tr', [
            m('th.ph2.pv2.tc.bb.b--mid-gray', [
              renderSelectionButton({
                checked: allMatchingSelected,
                mixed: someMatchingSelected,
                disabled: addEquipmentModal.loading() || matchingCount === 0,
                label: allMatchingSelected
                  ? 'Clear all ' + matchingCount + ' matching equipment'
                  : 'Select all ' + matchingCount + ' matching equipment',
                selectAll: true,
                onclick: function () {
                  setMatchingEquipmentSelected(!allMatchingSelected);
                }
              })
            ]),
            m('th.ph2.pv2.tl.bb.b--mid-gray.fw5', 'Name'),
            m('th.ph2.pv2.tl.bb.b--mid-gray.fw5', 'Class'),
            m('th.ph2.pv2.tl.bb.b--mid-gray.fw5', 'Location')
          ])
        ]),
        m('tbody', resultRecords.length
          ? resultRecords.map(function (equipmentRecord) {
            var selected = isSelected(equipmentRecord);
            var locationLabel = buildPathLabel(equipmentRecord);
            return m('tr', {
              key: equipmentRecord['@rid'],
              'data-equipment-rid': equipmentRecord['@rid']
            }, [
              m('td.ph2.pv2.tc.bb.b--near-black', selectedCellAttrs(selected), [
                renderSelectionButton({
                  checked: selected,
                  disabled: addEquipmentModal.loading(),
                  label: 'Select ' + buildPrimaryLabel(equipmentRecord),
                  rid: equipmentRecord['@rid'],
                  onclick: function (event) {
                    selectEquipment(equipmentRecord, !!event.shiftKey);
                  }
                })
              ]),
              m('td.ph2.pv2.tl.bb.b--near-black', selectedCellAttrs(selected), [
                m('span' + (selected ? '.blue' : '.near-white'), buildPrimaryLabel(equipmentRecord))
              ]),
              m('td.ph2.pv2.tl.bb.b--near-black', selectedCellAttrs(selected), humanizeClassName(equipmentRecord['@class']) || '—'),
              m('td.ph2.pv2.tl.bb.b--near-black', selectedCellAttrs(selected), locationLabel || m('span.moon-gray', '—'))
            ]);
          })
          : [m('tr', [
            m('td.pa3.moon-gray.tc', { colspan: 4 }, addEquipmentModal.loading()
              ? 'Loading equipment...'
              : 'No matching equipment found.')
          ])])
      ])
    ])
  ]);
};

addEquipmentModal.__testables = {
  buildPathLabel: buildPathLabel,
  buildPrimaryLabel: buildPrimaryLabel,
  buildLocationSelectors: buildLocationSelectors,
  filterClassOptions: filterClassOptions,
  formatLocationOptionLabel: formatLocationOptionLabel,
  getClassFilterOptions: getClassFilterOptions,
  getLocationScopeRid: getLocationScopeRid,
  humanizeClassName: humanizeClassName,
  normalizeLocationOptions: normalizeLocationOptions,
  normalizeResultRecords: normalizeResultRecords,
  normalizeSearchResponse: normalizeSearchResponse,
  matchingSelectedEquipment: matchingSelectedEquipment,
  searchEquipment: searchEquipment,
  selectEquipment: selectEquipment,
  setMatchingEquipmentSelected: setMatchingEquipmentSelected,
  setVisibleEquipmentSelected: setVisibleEquipmentSelected,
  showEquipmentPage: showEquipmentPage,
  visibleSelectedEquipment: visibleSelectedEquipment,
  updateLocationFilters: updateLocationFilters
};

module.exports = addEquipmentModal;
